import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from './CodeBlock.js';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={className}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>,
        li: ({ children }) => <li className="mb-0.5">{children}</li>,
        h1: ({ children }) => <h1 className="mb-2 text-base font-bold">{children}</h1>,
        h2: ({ children }) => <h2 className="mb-1.5 text-sm font-bold">{children}</h2>,
        h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold">{children}</h3>,
        code: ({ className: codeClassName, children, ...props }) => {
          // Detect block code (has language class) vs inline
          const match = /language-(\w+)/.exec(codeClassName || '');
          if (match) {
            // Block code — will be wrapped by <pre> override below
            return (
              <code data-language={match[1]} className={codeClassName} {...props}>
                {children}
              </code>
            );
          }
          return (
            <code className="rounded bg-black/10 px-1 py-0.5 text-xs font-mono dark:bg-white/10" {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => {
          // Extract language and text from the code child
          const child = children as React.ReactElement<{ 'data-language'?: string; children?: React.ReactNode }>;
          if (child?.props?.['data-language']) {
            const lang = child.props['data-language'];
            const text = String(child.props.children ?? '').replace(/\n$/, '');
            return <CodeBlock code={text} language={lang} />;
          }
          // Fallback: plain pre without language
          const text = String(
            (child?.props?.children ?? children) || ''
          ).replace(/\n$/, '');
          return <CodeBlock code={text} />;
        },
        table: ({ children }) => (
          <div className="mb-2 overflow-auto last:mb-0">
            <table className="min-w-full text-xs border-collapse">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-current/10">{children}</thead>,
        th: ({ children }) => <th className="px-2 py-1 text-left font-semibold">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 border-t border-current/5">{children}</td>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="underline opacity-80 hover:opacity-100">
            {children}
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="mb-2 border-l-2 border-current/20 pl-3 opacity-80 last:mb-0">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-2 border-current/10" />,
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
}
